import { useState } from "react";

import { computeWidget } from "../reportWidgets";
import ReportBuilder from "./ReportBuilder";

const formatAmount = (amount) =>
  `${new Intl.NumberFormat("ru-RU").format(Math.round(amount || 0))} ₸`;

const formatCount = (value) =>
  new Intl.NumberFormat("ru-RU").format(value || 0);

export default function ReportsPage({
  payments,
  templateColumns,
  paymentActions,
  actionColumns,
  reportWidgets,
  isAdmin,
  addReportWidget,
  updateReportWidget,
  removeReportWidget,
  moveReportWidget
}) {
  const [editing, setEditing] = useState(false);

  const meta = { templateColumns, paymentActions, actionColumns };
  const widgets = reportWidgets || [];
  const kpiWidgets = widgets.filter((w) => w.type === "kpi");
  const otherWidgets = widgets.filter((w) => w.type !== "kpi");

  const metricValue = (data, row) =>
    data.metric === "count" ? formatCount(row.count) : formatAmount(row.amount);

  const metricRaw = (data, row) =>
    data.metric === "count" ? row.count : row.amount;

  const renderKpi = (widget) => {
    const data = computeWidget(widget, payments, meta);
    if (!data) return null;

    const value = widget.agg === "count" ? formatCount(data.value) : formatAmount(data.value);

    return (
      <div className="report-kpi" key={widget.id}>
        <span>{widget.title}</span>
        <strong>{value}</strong>
        <small>{data.count} платежей</small>
      </div>
    );
  };

  const renderSection = (widget) => {
    const data = computeWidget(widget, payments, meta);
    if (!data) return null;

    return (
      <section className="report-section" key={widget.id}>
        <h2>{widget.title}</h2>

        {data.kind === "group" && data.rows.length === 0 && (
          <p className="report-empty">Нет данных.</p>
        )}

        {data.kind === "group" && data.chart === "table" && data.rows.length > 0 && (
          <table className="report-table">
            <tbody>
              {data.rows.map((row) => (
                <tr key={row.label}>
                  <td>{row.label}</td>
                  <td>{row.count}</td>
                  <td>{metricValue(data, row)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {data.kind === "group" && data.chart !== "table" &&
          data.rows.map((row) => (
            <div className="leader-row" key={row.label}>
              <div>
                <strong>{row.label}</strong>
                <small>{row.count} платежей</small>
              </div>
              <div className="leader-amount">
                <span>{metricValue(data, row)}</span>
                <i style={{ width: `${(metricRaw(data, row) / data.max) * 100}%` }} />
              </div>
            </div>
          ))}

        {data.kind === "dynamics" && (
          data.rows.length > 0 ? (
            <div className="dynamic-chart">
              {data.rows.map((row) => (
                <div className="dynamic-column" key={row.key}>
                  <span>{metricValue(data, row)}</span>
                  <div
                    className="dynamic-bar"
                    style={{ height: `${(metricRaw(data, row) / data.max) * 150}px` }}
                  />
                  <strong>{row.key.slice(5)}</strong>
                </div>
              ))}
            </div>
          ) : (
            <p className="report-empty">Нет платежей с датой.</p>
          )
        )}

        {data.kind === "actions" &&
          data.rows.map((status) => (
            <div className="status-report-row" key={status.label}>
              <div className="report-row-label">
                <span>{status.label}</span>
                <strong>{status.count}</strong>
              </div>
              <div className="report-track">
                <span style={{ width: `${status.percent}%`, backgroundColor: status.color }} />
              </div>
              <small>{formatAmount(status.amount)}</small>
            </div>
          ))}
      </section>
    );
  };

  return (
    <div className="reports-page">
      <div className="reports-head">
        <div>
          <h1>Отчеты</h1>
          <p>Аналитика по платежам и действиям</p>
        </div>

        {isAdmin && (
          <button className="report-configure-btn" onClick={() => setEditing((value) => !value)}>
            {editing ? "Готово" : "Настроить"}
          </button>
        )}
      </div>

      {editing && isAdmin && (
        <ReportBuilder
          widgets={widgets}
          templateColumns={templateColumns}
          paymentActions={paymentActions}
          actionColumns={actionColumns}
          addReportWidget={addReportWidget}
          updateReportWidget={updateReportWidget}
          removeReportWidget={removeReportWidget}
          moveReportWidget={moveReportWidget}
        />
      )}

      {kpiWidgets.length > 0 && (
        <div className="report-kpis">{kpiWidgets.map(renderKpi)}</div>
      )}

      {otherWidgets.length > 0 && (
        <div className="report-grid">{otherWidgets.map(renderSection)}</div>
      )}

      {widgets.length === 0 && (
        <p className="report-empty">
          Отчёт пуст.{isAdmin ? " Нажмите «Настроить», чтобы добавить виджеты." : ""}
        </p>
      )}
    </div>
  );
}
